import type { SupabaseClient } from '../../lib/supabase';
import type { Lead, LeadStage } from './types';
import { decodeCopySet } from './copyMessages';
import type { CopyEditCommand } from './copyEditConfirmation';
import { runJourneyCommand } from './journeyOperations';
import type { JourneyCommand } from './journeyOperations';
import { readProspectingAvailability } from './rolloutAvailability';

/** The deployed delete path, selected before any write, never an RPC fallback. */
export async function legacyDelete(
  client: SupabaseClient,
  table: 'leads' | 'lead_lists' | 'lead_list_memberships' | 'prospects',
  id: string,
) {
  if ((await readProspectingAvailability(client)).operations_enabled)
    throw new Error('The removal workflow changed. Refresh before continuing.');
  const { error } = await client.from(table).delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Database access for the Outbound Tracker. Every table sits behind the
 * sales_members gate in Row Level Security; nothing here is trusted.
 */

// One literal, deliberately unwrapped (PostgREST type inference).
// prettier-ignore
/**
 * The WHOLE row, and it has to stay the whole row.
 *
 * `Lead` is the generated type for every column, so a list one column short is
 * a lie the compiler believes until somebody regenerates the types. That has
 * now happened twice — `copy_set_id` and `round_id` on lead_activity in Drop
 * 21, and `capture_method_id` here — so the rule is written down in both
 * places: add a column to `leads`, add it here in the same drop.
 */
const LEAD_COLUMNS =
  'id, sheet_id, first_name, last_name, email, phone, company_name, title, stage_id, company_id, source, lead_source_id, capture_method_id, closure_reason_id, closed_on, nurture_active, nurture_since, nurture_note, email_hold_until, email_hold_note, bad_contact, last_touch_at, touch_count, notes, created_by, created_at, updated_by, updated_at';

const STAGE_COLUMNS = 'id, name, key, position, created_at';

function fail(message: string): never {
  throw new Error(message);
}

export async function fetchSalesAccess(client: SupabaseClient): Promise<boolean> {
  const { data, error } = await client.rpc('is_sales_member');
  if (error) {
    fail(error.message);
  }
  return data === true;
}

export async function fetchLeadStages(client: SupabaseClient): Promise<LeadStage[]> {
  const { data, error } = await client
    .from('lead_stages')
    .select(STAGE_COLUMNS)
    .order('position', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchLeads(client: SupabaseClient): Promise<Lead[]> {
  const { data, error } = await client
    .from('leads')
    .select(LEAD_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export interface LeadInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  /** Links the lead to an existing company row (the "add a lead here" flow). */
  companyId?: string;
  title: string;
  stageId: string;
  /**
   * Direction — inbound or outbound (batch 1b). Optional because the field is
   * a consent record and a blank is honest: "we have not said". Once set to
   * inbound the database refuses to change it on any ordinary path, so the
   * form asks rather than defaulting.
   */
  direction?: 'inbound' | 'outbound' | null;
  /** Which door they came through. Null when nobody chose. */
  leadSourceId?: string | null;
}

export async function createLead(
  client: SupabaseClient,
  input: LeadInput & { createdBy: string },
): Promise<Lead> {
  const { data, error } = await client
    .from('leads')
    .insert({
      first_name: input.firstName.trim(),
      last_name: input.lastName.trim(),
      email: input.email.trim(),
      phone: input.phone.trim(),
      company_name: input.companyName.trim(),
      company_id: input.companyId ?? null,
      title: input.title.trim(),
      stage_id: input.stageId,
      source: input.direction ?? null,
      lead_source_id: input.leadSourceId ?? null,
      created_by: input.createdBy,
    })
    .select(LEAD_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export type LeadUpdate = Partial<{
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  title: string;
  stage_id: string;
  source: Lead['source'];
  notes: string;
  updated_by: string;
  // Batch 1b. lead_source_id and closure_reason_id are ordinary editable
  // fields; the nurture pair is written through useSetNurture so that turning
  // it off always clears the note with it.
  //
  // Batch 49 took lead_source_id off every form — no weekly question needed
  // it. The column and its data stay, and so does this, so the field can come
  // back without a migration if a real question for it ever appears.
  lead_source_id: string | null;
  /**
   * HOW their details reached us, which is not WHERE they came from.
   *
   * Alex: "Social media > Instagram" is the source, "ManyChat DM" is the
   * capture method. An ordinary editable field beside lead_source_id, and
   * nullable for the same reason — "Not said" is an answer somebody can give.
   */
  capture_method_id: string | null;
  /** Batch 49. The one writer that makes the Bad contact badge and chip true. */
  bad_contact: boolean;
  closure_reason_id: string | null;
  /**
   * Historical dates belong to the atomic merge RPC, not a browser payload.
   * Ordinary updates leave the date to leads_closure_reason_guard, which
   * stamps it on the way into Closed and clears it
   * on the way out, and it deliberately never overwrites a date already there,
   * which is what lets the merge preserve the day she actually said stop.
   *
   * There is still no UI that writes this, so a close recorded on the wrong
   * day cannot yet be corrected. Known, and not this drop.
   */
  closed_on: string | null;
}>;

export async function updateLead(
  client: SupabaseClient,
  leadId: string,
  changes: LeadUpdate,
): Promise<Lead> {
  const { data, error } = await client
    .from('leads')
    .update(changes)
    .eq('id', leadId)
    .select(LEAD_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('sales_not_permitted');
  }
  return data;
}

export async function deleteLead(
  client: SupabaseClient,
  command: JourneyCommand | string,
): Promise<void> {
  if (typeof command === 'string') return legacyDelete(client, 'leads', command);
  if (command.kind !== 'cleanup' || command.table !== 'leads') fail('journey_command_mismatch');
  await runJourneyCommand(client, command);
}

// prettier-ignore
const COMPANY_COLUMNS = 'id, sheet_id, name, website, industry, deal_value, stage_override_id, notes, created_by, created_at, updated_at';
const LIST_COLUMNS = 'id, sheet_id, name, type, created_by, created_at';
// prettier-ignore
const MEMBERSHIP_COLUMNS = 'id, sheet_id, list_id, lead_id, copy_set_id, touch1_status, touch1_sent_on, touch1_source, touch2_status, touch2_sent_on, touch2_source, touch3_status, touch3_sent_on, touch3_source, touch4_status, touch4_sent_on, touch4_source, touch5_status, touch5_sent_on, touch5_source, touch6_status, touch6_sent_on, touch6_source, touch7_status, touch7_sent_on, touch7_source, touch8_status, touch8_sent_on, touch8_source, touch2_due_on, touch3_due_on, touch4_due_on, touch5_due_on, touch6_due_on, touch7_due_on, touch8_due_on, seq_status, reply_touch, reply_tag, reply_date, added_at';
// prettier-ignore
const COPY_COLUMNS = 'id, sheet_id, name, description, type, channel, archived_at, e1_subject, e1_body, e2_subject, e2_body, e3_subject, e3_body, e4_subject, e4_body, e5_subject, e5_body, e6_subject, e6_body, e7_subject, e7_body, e8_subject, e8_body, created_by, created_at, updated_at, updated_by, is_one_off, review_state, reviewed_by, reviewed_at, message_revision';
const COPY_CATALOG_COLUMNS =
  'id, sheet_id, name, description, type, channel, archived_at, e1_subject, e1_body, e2_subject, e2_body, e3_subject, e3_body, e4_subject, e4_body, e5_subject, e5_body, e6_subject, e6_body, e7_subject, e7_body, e8_subject, e8_body, created_by, created_at, updated_at, updated_by, is_one_off, review_state, reviewed_by, reviewed_at, message_revision, message_used';

export async function fetchCompanies(client: SupabaseClient) {
  const { data, error } = await client
    .from('companies')
    .select(COMPANY_COLUMNS)
    .order('name', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export type CompanyUpdate = Partial<{
  name: string;
  website: string;
  industry: string;
  deal_value: number | null;
  stage_override_id: string | null;
  notes: string;
}>;

export async function createCompany(
  client: SupabaseClient,
  input: { name: string; website: string; industry: string; createdBy: string },
) {
  const { data, error } = await client
    .from('companies')
    .insert({
      name: input.name.trim(),
      website: input.website.trim(),
      industry: input.industry.trim(),
      created_by: input.createdBy,
    })
    .select(COMPANY_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function deleteCompany(client: SupabaseClient, companyId: string) {
  const { data, error } = await client.from('companies').delete().eq('id', companyId).select('id');
  if (error) {
    fail(error.message);
  }
  if ((data ?? []).length === 0) {
    fail('sales_not_permitted');
  }
}

/**
 * Folds one company into another: leads linked by id or by (unlinked) name
 * move to the keeper, tasks follow, the loser goes away. The keeper's own
 * fields are untouched — merging is about the relationships.
 */
export async function mergeCompanies(
  client: SupabaseClient,
  keeper: { id: string; name: string },
  loser: { id: string; name: string },
): Promise<void> {
  const byId = await client
    .from('leads')
    .update({ company_id: keeper.id, company_name: keeper.name })
    .eq('company_id', loser.id);
  if (byId.error) {
    fail(byId.error.message);
  }
  const byName = await client
    .from('leads')
    .update({ company_id: keeper.id, company_name: keeper.name })
    .is('company_id', null)
    .ilike('company_name', loser.name);
  if (byName.error) {
    fail(byName.error.message);
  }
  const tasks = await client
    .from('sales_tasks')
    .update({ company_id: keeper.id })
    .eq('company_id', loser.id);
  if (tasks.error) {
    fail(tasks.error.message);
  }
  await deleteCompany(client, loser.id);
}

export async function updateCompany(
  client: SupabaseClient,
  companyId: string,
  changes: CompanyUpdate,
) {
  const { data, error } = await client
    .from('companies')
    .update(changes)
    .eq('id', companyId)
    .select(COMPANY_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('sales_not_permitted');
  }
  return data;
}

export async function fetchLeadLists(client: SupabaseClient) {
  const { data, error } = await client
    .from('lead_lists')
    .select(LIST_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function createLeadList(
  client: SupabaseClient,
  name: string,
  type: string,
  createdBy: string,
) {
  const { data, error } = await client
    .from('lead_lists')
    .insert({ name: name.trim(), type, created_by: createdBy })
    .select(LIST_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function renameLeadList(client: SupabaseClient, listId: string, name: string) {
  const { data, error } = await client
    .from('lead_lists')
    .update({ name: name.trim() })
    .eq('id', listId)
    .select(LIST_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('sales_not_permitted');
  }
  return data;
}

/** Memberships cascade with the list; leads themselves are untouched. */
export async function deleteLeadList(
  client: SupabaseClient,
  command: JourneyCommand | string,
): Promise<void> {
  if (typeof command === 'string') return legacyDelete(client, 'lead_lists', command);
  if (command.kind !== 'cleanup' || command.table !== 'lead_lists')
    fail('journey_command_mismatch');
  await runJourneyCommand(client, command);
}

/** Adds leads to a list; pairs already in the list are skipped quietly. */
export async function addLeadsToList(
  client: SupabaseClient,
  listId: string,
  leadIds: string[],
): Promise<number> {
  if (leadIds.length === 0) {
    return 0;
  }
  const { data, error } = await client
    .from('lead_list_memberships')
    .upsert(
      leadIds.map((leadId) => ({ list_id: listId, lead_id: leadId })),
      { onConflict: 'list_id,lead_id', ignoreDuplicates: true },
    )
    .select('id');
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data.length : 0;
}

export async function removeMembership(
  client: SupabaseClient,
  command: JourneyCommand | string,
): Promise<void> {
  if (typeof command === 'string') return legacyDelete(client, 'lead_list_memberships', command);
  if (command.kind !== 'cleanup' || command.table !== 'lead_list_memberships')
    fail('journey_command_mismatch');
  await runJourneyCommand(client, command);
}

export async function fetchMemberships(client: SupabaseClient, listId: string) {
  const { data, error } = await client
    .from('lead_list_memberships')
    .select(MEMBERSHIP_COLUMNS)
    .eq('list_id', listId)
    .order('added_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchCopySets(client: SupabaseClient, messagesEnabled = true) {
  const query = messagesEnabled
    ? client.from('copy_set_catalog').select(COPY_CATALOG_COLUMNS)
    : client.from('copy_sets').select(COPY_COLUMNS);
  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data.map((row) => decodeCopySet(row, messagesEnabled)) : [];
}

export async function saveCopyEdit(client: SupabaseClient, command: CopyEditCommand) {
  const { data, error } = await client.rpc('edit_copy_set_version', {
    p_copy: command.copySetId,
    p_expected_revision: command.expectedRevision,
    p_fields: { ...command.fields },
    p_request: command.requestId,
    p_confirmed: command.confirmedUsedEdit,
  });
  if (error) fail(error.message);
  return decodeCopySet(data);
}

export type CopySetInput = Partial<{
  review_state: 'draft' | 'library';
  archived_at: string | null;
  name: string;
  description: string;
  type: string;
  channel: string;
  e1_subject: string;
  e1_body: string;
  e2_subject: string;
  e2_body: string;
  e3_subject: string;
  e3_body: string;
  e4_subject: string;
  e4_body: string;
  e5_subject: string;
  e5_body: string;
  e6_subject: string;
  e6_body: string;
  e7_subject: string;
  e7_body: string;
  e8_subject: string;
  e8_body: string;
}>;

export async function createCopySet(
  client: SupabaseClient,
  input: CopySetInput & { name: string; createdBy: string },
) {
  const { createdBy, ...fields } = input;
  const { data, error } = await client
    .from('copy_sets')
    .insert({ ...fields, created_by: createdBy })
    .select(COPY_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function updateCopySet(
  client: SupabaseClient,
  copySetId: string,
  changes: CopySetInput,
) {
  const { data, error } = await client
    .from('copy_sets')
    .update(changes)
    .eq('id', copySetId)
    .select(COPY_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('sales_not_permitted');
  }
  return data;
}

export type MembershipUpdate = Partial<{
  copy_set_id: string | null;
  touch1_status: string;
  touch1_sent_on: string | null;
  touch2_status: string;
  touch2_sent_on: string | null;
  touch3_status: string;
  touch3_sent_on: string | null;
  touch4_status: string;
  touch4_sent_on: string | null;
  touch5_status: string;
  touch5_sent_on: string | null;
  touch6_status: string;
  touch6_sent_on: string | null;
  touch7_status: string;
  touch7_sent_on: string | null;
  touch8_status: string;
  touch8_sent_on: string | null;
  // A due date set by hand; null is automatic (touchDueOn in types.ts).
  touch2_due_on: string | null;
  touch3_due_on: string | null;
  touch4_due_on: string | null;
  touch5_due_on: string | null;
  touch6_due_on: string | null;
  touch7_due_on: string | null;
  touch8_due_on: string | null;
  seq_status: 'active' | 'bounced' | 'opted_out' | 'replied';
  reply_touch: number | null;
  reply_tag: string;
  reply_date: string | null;
}>;

export async function updateMembership(
  client: SupabaseClient,
  membershipId: string,
  changes: MembershipUpdate,
) {
  const { data, error } = await client
    .from('lead_list_memberships')
    .update(changes)
    .eq('id', membershipId)
    .select(MEMBERSHIP_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('sales_not_permitted');
  }
  return data;
}

export async function fetchAllMemberships(client: SupabaseClient) {
  const { data, error } = await client
    .from('lead_list_memberships')
    .select(MEMBERSHIP_COLUMNS)
    .order('added_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

// prettier-ignore
const TASK_COLUMNS = 'id, sheet_id, title, due_on, assigned_to, lead_id, company_id, status, created_by, created_at, completed_at';
// prettier-ignore
const LINKEDIN_COLUMNS = 'id, sheet_id, entry_date, connections_sent, cs_notes, new_connections, nc_notes, dms_sent, dm_notes, messages_received, mr_notes, created_by, created_at';

export async function fetchSalesTasks(client: SupabaseClient) {
  const { data, error } = await client
    .from('sales_tasks')
    .select(TASK_COLUMNS)
    .order('due_on', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export type SalesTaskInput = {
  title: string;
  due_on: string | null;
  assigned_to: string | null;
  lead_id: string | null;
  company_id: string | null;
};

export async function createSalesTask(
  client: SupabaseClient,
  input: SalesTaskInput & { createdBy: string },
) {
  const { createdBy, ...fields } = input;
  const { data, error } = await client
    .from('sales_tasks')
    .insert({ ...fields, created_by: createdBy })
    .select(TASK_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export type SalesTaskUpdate = Partial<
  SalesTaskInput & { status: 'open' | 'done'; completed_at: string | null }
>;

export async function updateSalesTask(
  client: SupabaseClient,
  taskId: string,
  changes: SalesTaskUpdate,
) {
  const { data, error } = await client
    .from('sales_tasks')
    .update(changes)
    .eq('id', taskId)
    .select(TASK_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('sales_not_permitted');
  }
  return data;
}

export async function deleteSalesTask(client: SupabaseClient, taskId: string): Promise<void> {
  const { error } = await client.from('sales_tasks').delete().eq('id', taskId);
  if (error) {
    fail(error.message);
  }
}

export async function fetchLinkedInDays(client: SupabaseClient) {
  const { data, error } = await client
    .from('linkedin_activity')
    .select(LINKEDIN_COLUMNS)
    .order('entry_date', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export type LinkedInDayInput = {
  entry_date: string;
  connections_sent: number;
  cs_notes: string;
  new_connections: number;
  nc_notes: string;
  dms_sent: number;
  dm_notes: string;
  messages_received: number;
  mr_notes: string;
};

/** One entry per day: saving an existing date edits that day. */
export async function saveLinkedInDay(
  client: SupabaseClient,
  input: LinkedInDayInput & { createdBy: string },
) {
  const { createdBy, ...fields } = input;
  const { data: existing, error: findErr } = await client
    .from('linkedin_activity')
    .select('id')
    .eq('entry_date', fields.entry_date)
    .maybeSingle();
  if (findErr) {
    fail(findErr.message);
  }
  if (existing) {
    const { data, error } = await client
      .from('linkedin_activity')
      .update(fields)
      .eq('id', existing.id)
      .select(LINKEDIN_COLUMNS)
      .maybeSingle();
    if (error) {
      fail(error.message);
    }
    return data;
  }
  const { data, error } = await client
    .from('linkedin_activity')
    .insert({ ...fields, created_by: createdBy })
    .select(LINKEDIN_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export interface BulkLeadRow {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  title: string;
}

/**
 * What a whole imported batch shares.
 *
 * Kyu: "Paste-import batches apply one shared Direction, Source, and Category
 * selection." One choice for the batch rather than a column per row, because
 * a pasted list comes from one place — a Typeform export, a conference list —
 * and asking per row would mean nobody sets it at all.
 */
export interface BulkLeadDefaults {
  direction: 'inbound' | 'outbound' | null;
  leadSourceId: string | null;
}

/** Inserts imported leads in one call; the database still enforces dedupe. */
export async function createLeadsBulk(
  client: SupabaseClient,
  rows: BulkLeadRow[],
  stageId: string,
  createdBy: string,
  defaults: BulkLeadDefaults = { direction: null, leadSourceId: null },
): Promise<{ count: number; ids: string[] }> {
  const { data, error } = await client
    .from('leads')
    .insert(
      rows.map((row) => ({
        first_name: row.firstName,
        last_name: row.lastName,
        email: row.email,
        phone: row.phone,
        company_name: row.companyName,
        title: row.title,
        stage_id: stageId,
        created_by: createdBy,
        source: defaults.direction,
        lead_source_id: defaults.leadSourceId,
      })),
    )
    .select('id');
  if (error) {
    fail(error.message);
  }
  const ids = Array.isArray(data) ? data.map((row: { id: string }) => row.id) : [];
  return { count: ids.length, ids };
}

/**
 * One atomic database operation, not a series of independently committed
 * writes. Consent, row counts, child conflicts and preserved evidence are
 * checked server-side. Retries retain the explicit reason and request UUID.
 */
export async function mergeLeads(client: SupabaseClient, command: JourneyCommand): Promise<void> {
  if (command.kind !== 'merge') fail('journey_command_mismatch');
  await runJourneyCommand(client, command);
}
